import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Plus, X } from "lucide-react";
import { api, type AutoReplyRule, type BotCommand } from "@/lib/api";
import { useCan } from "@/lib/shell-context";
import { Button, ErrorNote, Field, Input, Loading, Panel, Select } from "@/components/primitives";
import { RichText, TgHtml } from "@/components/rich-text";
import { LanguageTabs, TranslationNote } from "@/components/language-tabs";
import { newKey, useLanguages } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { escapeHtml } from "@/lib/tg-format";
import { languageName, tgHtmlToPlain } from "@kickwin/shared";

/**
 * Bottom menu (decision 38).
 *
 * Buttons under the player's text box — Telegram's reply keyboard. Telegram
 * only lets such a button send its own label, so the bot recognises the label
 * and does what is set here: open a link, answer as a command or an auto
 * reply, or hand the player to CS. Taps never reach the Topic, except "Asks
 * our team". The menu arrives when a player taps Start, and rides along on
 * any reply that has no buttons of its own, so changes reach players with the
 * next message the bot sends them.
 */

type Action =
  | { kind: "link"; url: string; message?: string | undefined }
  | { kind: "command"; commandId: number }
  | { kind: "rule"; ruleId: number }
  | { kind: "help"; reply?: string | undefined };

interface FooterButton {
  text: string;
  /** Stable id; translations are keyed by it (decision 46). */
  k?: string | undefined;
  action: Action;
}

/** One language's words for the menu; anything left out uses the default. */
interface FooterTranslation {
  intro?: string | undefined;
  nodes?:
    | Record<
        string,
        { label?: string | undefined; message?: string | undefined; reply?: string | undefined }
      >
    | undefined;
}

interface Footer {
  v: 1;
  /** Its texts are Telegram HTML (decision 45). */
  format?: "html" | undefined;
  intro?: string | undefined;
  rows: FooterButton[][];
  i18n?: Record<string, FooterTranslation> | undefined;
}

/** Every button with a stable id, so translations can find it. */
function withKeys(footer: Footer): Footer {
  return {
    ...footer,
    rows: footer.rows.map((row) => row.map((b) => (b.k ? b : { ...b, k: newKey() }))),
  };
}

/** The menu as a player of that language sees it. */
function localizeFooter(footer: Footer, lang: string | null): Footer {
  const t = lang ? footer.i18n?.[lang] : undefined;
  if (!t) return footer;
  return {
    ...footer,
    intro: filled(t.intro) ? t.intro : footer.intro,
    rows: footer.rows.map((row) =>
      row.map((b) => {
        const n = b.k ? t.nodes?.[b.k] : undefined;
        if (!n) return b;
        const a = b.action;
        return {
          ...b,
          text: n.label?.trim() || b.text,
          action:
            a.kind === "link" && filled(n.message)
              ? { ...a, message: n.message }
              : a.kind === "help" && filled(n.reply)
                ? { ...a, reply: n.reply }
                : a,
        };
      }),
    ),
  };
}

/** Drops empty words, and languages left with none, before saving. */
function pruneFooterTranslations(
  i18n: Footer["i18n"],
): Record<string, FooterTranslation> | undefined {
  const out: Record<string, FooterTranslation> = {};
  for (const [lang, t] of Object.entries(i18n ?? {})) {
    const nodes: NonNullable<FooterTranslation["nodes"]> = {};
    for (const [k, n] of Object.entries(t.nodes ?? {})) {
      const kept = {
        ...(n.label?.trim() ? { label: n.label.trim() } : {}),
        ...(filled(n.message) ? { message: n.message } : {}),
        ...(filled(n.reply) ? { reply: n.reply } : {}),
      };
      if (Object.keys(kept).length > 0) nodes[k] = kept;
    }
    const kept: FooterTranslation = {
      ...(filled(t.intro) ? { intro: t.intro } : {}),
      ...(Object.keys(nodes).length > 0 ? { nodes } : {}),
    };
    if (Object.keys(kept).length > 0) out[lang] = kept;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** A menu saved before formatting, with its texts as Telegram HTML. */
function asHtml(footer: Footer): Footer {
  if (footer.format === "html") return withKeys(footer);
  const esc = (t: string | undefined) => (t === undefined ? undefined : escapeHtml(t));
  return {
    ...footer,
    format: "html",
    intro: esc(footer.intro),
    rows: footer.rows.map((row) =>
      row.map((b) =>
        b.action.kind === "link"
          ? { ...b, action: { ...b.action, message: esc(b.action.message) } }
          : b.action.kind === "help"
            ? { ...b, action: { ...b.action, reply: esc(b.action.reply) } }
            : b,
      ),
    ),
  };
}

/** Whether formatted text has anything to read. */
const filled = (html: string | undefined): html is string => !!html && !!tgHtmlToPlain(html).trim();

const MAX_ROWS = 6;
const MAX_PER_ROW = 4;
const DEFAULT_INTRO = "👇 Quick menu below — tap a button anytime.";

export function FooterPage() {
  const { botId } = useParams();
  const canEdit = useCan("footer", "manage");
  const queryClient = useQueryClient();

  const saved = useQuery({
    queryKey: ["footer", botId],
    queryFn: () => api.get<Footer | null>(`/bots/${botId}/footer`),
  });
  const commands = useQuery({
    queryKey: ["commands", botId],
    queryFn: () => api.get<BotCommand[]>(`/bots/${botId}/commands`),
  });
  const rules = useQuery({
    queryKey: ["auto-replies", botId],
    queryFn: () => api.get<AutoReplyRule[]>(`/bots/${botId}/auto-replies`),
  });

  if (saved.isLoading) return <Loading />;
  if (saved.error) return <ErrorNote error={saved.error} />;

  return (
    <Editor
      key={JSON.stringify(saved.data ?? null)}
      initial={saved.data ?? { v: 1, rows: [] }}
      commands={commands.data ?? []}
      rules={rules.data ?? []}
      canEdit={canEdit}
      onSaved={() => void queryClient.invalidateQueries({ queryKey: ["footer", botId] })}
      botId={botId}
    />
  );
}

function Editor({
  initial,
  commands,
  rules,
  canEdit,
  onSaved,
  botId,
}: {
  initial: Footer;
  commands: BotCommand[];
  rules: AutoReplyRule[];
  canEdit: boolean;
  onSaved: () => void;
  botId: string | undefined;
}) {
  const [footer, setFooter] = useState<Footer>(() => withKeys(asHtml(initial)));
  const [editing, setEditing] = useState<{ row: number; col: number | null } | null>(null);
  const [translating, setTranslating] = useState<{ row: number; col: number } | null>(null);
  const [touched, setTouched] = useState(false);
  const languages = useLanguages(botId);
  const [lang, setLang] = useState<string | null>(null);

  const setRows = (rows: FooterButton[][]) => setFooter({ ...footer, rows });
  const translation: FooterTranslation = (lang && footer.i18n?.[lang]) || {};
  const setTranslation = (t: FooterTranslation) =>
    lang && setFooter({ ...footer, i18n: { ...footer.i18n, [lang]: t } });
  const shown = localizeFooter(footer, lang);

  // Labels must differ within every language, or a tap could not be told apart.
  const clash = [null, ...(languages?.extra ?? [])].find((l) => {
    const labels = localizeFooter(footer, l)
      .rows.flat()
      .map((b) => b.text.trim());
    return new Set(labels).size !== labels.length;
  });
  const problem =
    clash === undefined
      ? null
      : `Two buttons have the same label${clash ? ` in ${languageName(clash)}` : ""}; the bot could not tell them apart.`;

  const save = useMutation({
    mutationFn: () =>
      api.put(`/bots/${botId}/footer`, {
        v: 1,
        format: "html",
        ...(filled(footer.intro) ? { intro: footer.intro } : {}),
        rows: footer.rows,
        ...(pruneFooterTranslations(footer.i18n)
          ? { i18n: pruneFooterTranslations(footer.i18n) }
          : {}),
      }),
    onSuccess: onSaved,
  });

  const describe = (action: Action) =>
    action.kind === "link"
      ? "↗ Opens a link"
      : action.kind === "command"
        ? `/${commands.find((c) => c.id === action.commandId)?.command ?? "missing command"}`
        : action.kind === "rule"
          ? `Auto reply: ${rules.find((r) => r.id === action.ruleId)?.name ?? "missing rule"}`
          : "🙋 Asks our team";

  const moveRow = (r: number, by: -1 | 1) => {
    const rows = [...footer.rows];
    const [row] = rows.splice(r, 1);
    rows.splice(r + by, 0, row!);
    setRows(rows);
  };
  const moveButton = (r: number, c: number, by: -1 | 1) =>
    setRows(
      footer.rows.map((row, ri) => {
        if (ri !== r) return row;
        const next = [...row];
        const [b] = next.splice(c, 1);
        next.splice(c + by, 0, b!);
        return next;
      }),
    );
  const remove = (r: number, c: number) =>
    setRows(
      footer.rows
        .map((row, ri) => (ri === r ? row.filter((_, ci) => ci !== c) : row))
        .filter((row) => row.length > 0),
    );
  const put = (edited: FooterButton) => {
    if (!editing) return;
    const { row, col } = editing;
    // The id stays with the button, so its translations do too.
    const before = col === null ? undefined : footer.rows[row]?.[col];
    const button = { ...edited, k: before?.k ?? newKey() };
    if (row >= footer.rows.length) setRows([...footer.rows, [button]]);
    else
      setRows(
        footer.rows.map((rw, ri) =>
          ri !== row
            ? rw
            : col === null
              ? [...rw, button]
              : rw.map((b, ci) => (ci === col ? button : b)),
        ),
      );
    setEditing(null);
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
      <Panel title="Bottom menu">
        <fieldset disabled={!canEdit} className="grid gap-4 px-5 py-3">
          <p className="text-muted-foreground">
            Buttons under the player's text box. Players get them when they tap Start, and with any
            reply that has no buttons of its own. Taps never appear in the Topic, except “Asks our
            team”, which opens it.
          </p>

          <LanguageTabs
            languages={languages}
            value={lang}
            onChange={(next) => {
              setLang(next);
              setEditing(null);
              setTranslating(null);
            }}
            translated={(l) => !!pruneFooterTranslations(footer.i18n)?.[l]}
          />
          {lang && <TranslationNote lang={lang} />}

          <Field group label="Message sent with the menu on Start">
            <RichText
              key={lang ?? ""}
              compact
              value={lang ? (translation.intro ?? "") : (footer.intro ?? "")}
              onChange={(intro) =>
                lang
                  ? setTranslation({ ...translation, intro: intro || undefined })
                  : setFooter({ ...footer, intro: intro || undefined })
              }
              placeholder={lang ? plainHint(footer.intro) || DEFAULT_INTRO : DEFAULT_INTRO}
              label="Message sent with the menu on Start"
              disabled={!canEdit}
            />
          </Field>

          <div className="grid gap-2">
            <div className="font-medium">Buttons</div>
            {shown.rows.map((row, r) => (
              <div key={r} className="flex items-stretch gap-1.5">
                <div className="grid min-w-0 flex-1 gap-1.5 sm:auto-cols-fr sm:grid-flow-col">
                  {row.map((button, c) => (
                    <div
                      key={c}
                      className="grid min-w-0 gap-1 rounded-md border border-border bg-card p-1.5"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          lang ? setTranslating({ row: r, col: c }) : setEditing({ row: r, col: c })
                        }
                        className="min-h-9 truncate rounded bg-secondary px-2 py-1.5 text-center font-medium"
                        title={lang ? "Translate this button" : "Edit this button"}
                      >
                        {button.text}
                      </button>
                      <div className="flex items-center gap-0.5">
                        <span className="mr-auto truncate px-1 text-muted-foreground">
                          {describe(button.action)}
                        </span>
                        {!lang && (
                          <>
                            <Icon
                              label="Move left"
                              disabled={c === 0}
                              onClick={() => moveButton(r, c, -1)}
                            >
                              <ArrowLeft />
                            </Icon>
                            <Icon
                              label="Move right"
                              disabled={c === row.length - 1}
                              onClick={() => moveButton(r, c, 1)}
                            >
                              <ArrowRight />
                            </Icon>
                            <Icon label="Remove button" onClick={() => remove(r, c)}>
                              <X />
                            </Icon>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  className={cn("flex shrink-0 flex-col justify-center gap-0.5", lang && "hidden")}
                >
                  <Icon label="Move row up" disabled={r === 0} onClick={() => moveRow(r, -1)}>
                    <ArrowUp />
                  </Icon>
                  {row.length < MAX_PER_ROW && (
                    <Icon
                      label="Add a button to this row"
                      onClick={() => setEditing({ row: r, col: null })}
                    >
                      <Plus />
                    </Icon>
                  )}
                  <Icon
                    label="Move row down"
                    disabled={r === footer.rows.length - 1}
                    onClick={() => moveRow(r, 1)}
                  >
                    <ArrowDown />
                  </Icon>
                </div>
              </div>
            ))}
            {!lang && footer.rows.length < MAX_ROWS && (
              <button
                type="button"
                onClick={() => setEditing({ row: footer.rows.length, col: null })}
                className="flex h-10 items-center justify-center gap-1.5 rounded-md border border-dashed border-border text-muted-foreground hover:border-ring hover:text-foreground"
              >
                <Plus className="size-4" /> Add a row of buttons
              </button>
            )}
          </div>
        </fieldset>

        {canEdit && (
          <div className="flex flex-wrap items-center gap-3 border-t border-border px-5 py-3">
            <Button
              disabled={save.isPending}
              onClick={() => {
                setTouched(true);
                if (!problem) save.mutate();
              }}
            >
              {save.isPending ? "Saving" : "Save bottom menu"}
            </Button>
            {touched && problem && <span className="text-destructive">{problem}</span>}
            {!problem && save.isSuccess && !save.isPending && (
              <span className="text-ok">Saved. Players get it with the next message.</span>
            )}
          </div>
        )}
        {save.error && (
          <div className="px-5 pb-3">
            <ErrorNote error={save.error} />
          </div>
        )}
      </Panel>

      <Preview footer={shown} />

      {lang && translating && footer.rows[translating.row]?.[translating.col]?.k && (
        <TranslateDialog
          key={`${lang}:${translating.row}:${translating.col}`}
          button={footer.rows[translating.row]![translating.col]!}
          words={translation.nodes?.[footer.rows[translating.row]![translating.col]!.k!] ?? {}}
          onCancel={() => setTranslating(null)}
          onSave={(words) => {
            const k = footer.rows[translating.row]![translating.col]!.k!;
            setTranslation({ ...translation, nodes: { ...translation.nodes, [k]: words } });
            setTranslating(null);
          }}
        />
      )}

      <ButtonDialog
        key={editing ? `${editing.row}:${editing.col}` : "closed"}
        open={editing !== null}
        initial={
          editing && editing.col !== null ? (footer.rows[editing.row]?.[editing.col] ?? null) : null
        }
        commands={commands}
        rules={rules}
        onCancel={() => setEditing(null)}
        onSave={put}
      />
    </div>
  );
}

function ButtonDialog({
  open,
  initial,
  commands,
  rules,
  onCancel,
  onSave,
}: {
  open: boolean;
  initial: FooterButton | null;
  commands: BotCommand[];
  rules: AutoReplyRule[];
  onCancel: () => void;
  onSave: (button: FooterButton) => void;
}) {
  const a = initial?.action;
  const [text, setText] = useState(initial?.text ?? "");
  const [kind, setKind] = useState<Action["kind"]>(a?.kind ?? "help");
  const [url, setUrl] = useState(a?.kind === "link" ? a.url : "");
  const [message, setMessage] = useState(a?.kind === "link" ? (a.message ?? "") : "");
  const [commandId, setCommandId] = useState<number | null>(
    a?.kind === "command" ? a.commandId : null,
  );
  const [ruleId, setRuleId] = useState<number | null>(a?.kind === "rule" ? a.ruleId : null);
  const [reply, setReply] = useState(a?.kind === "help" ? (a.reply ?? "") : "");
  const [touched, setTouched] = useState(false);

  const problem = !text.trim()
    ? "Give the button a label."
    : text.trim().startsWith("/")
      ? "A label cannot start with /."
      : kind === "link" && !/^(https?|tg):\/\/\S+/.test(url.trim())
        ? "Enter a full link starting with https://."
        : kind === "command" && commandId === null
          ? "Choose a command."
          : kind === "rule" && ruleId === null
            ? "Choose an auto reply."
            : null;

  const save = () => {
    setTouched(true);
    if (problem) return;
    const action: Action =
      kind === "link"
        ? { kind, url: url.trim(), ...(filled(message) ? { message } : {}) }
        : kind === "command"
          ? { kind, commandId: commandId! }
          : kind === "rule"
            ? { kind, ruleId: ruleId! }
            : { kind, ...(filled(reply) ? { reply } : {}) };
    onSave({ text: text.trim(), action });
  };

  const option = (value: Action["kind"], label: string, hint: string) => (
    <label className="flex items-start gap-2">
      <input
        type="radio"
        name="footer-action"
        checked={kind === value}
        onChange={() => setKind(value)}
        className="mt-1"
      />
      <span>
        {label}
        <span className="block text-muted-foreground">{hint}</span>
      </span>
    </label>
  );

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/45" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed top-1/2 left-1/2 z-50 grid max-h-[90dvh] w-[min(460px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-lg border border-border bg-card p-4 shadow-xl"
        >
          <Dialog.Title className="font-semibold">
            {initial ? "Edit bottom button" : "New bottom button"}
          </Dialog.Title>
          <Field label="Label">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="🙋 更多问题"
              maxLength={64}
              autoFocus
            />
          </Field>
          <fieldset className="grid gap-2.5">
            <legend className="mb-1.5 font-medium">When tapped</legend>
            {option(
              "help",
              "Asks our team",
              "Opens the player's Topic and tells CS they want help.",
            )}
            {kind === "help" && (
              <Field group label="Reply to the player (optional)">
                <RichText
                  compact
                  label="Reply to the player"
                  value={reply}
                  onChange={setReply}
                  placeholder="✅ Thanks — our team has been notified and will reply here shortly."
                />
              </Field>
            )}
            {option(
              "command",
              "Answers as a command",
              "Sends what that command sends, buttons included.",
            )}
            {kind === "command" && (
              <Select
                aria-label="Command"
                className="w-full"
                value={commandId ?? ""}
                onChange={(e) => setCommandId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Choose a command</option>
                {commands.map((c) => (
                  <option key={c.id} value={c.id}>
                    /{c.command} — {c.description}
                  </option>
                ))}
              </Select>
            )}
            {option(
              "rule",
              "Answers as an auto reply",
              "Sends that rule's reply, whatever it matches on.",
            )}
            {kind === "rule" && (
              <Select
                aria-label="Auto reply"
                className="w-full"
                value={ruleId ?? ""}
                onChange={(e) => setRuleId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Choose an auto reply</option>
                {rules.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {r.isActive ? "" : " (off)"}
                  </option>
                ))}
              </Select>
            )}
            {option(
              "link",
              "Opens a link",
              "Keyboard buttons cannot open links, so the bot replies with one to tap.",
            )}
            {kind === "link" && (
              <div className="grid gap-2">
                <Input
                  className="mono"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://kickwin.site"
                  aria-label="Link"
                />
                <RichText
                  compact
                  value={message}
                  onChange={setMessage}
                  placeholder="Message above the link (optional)"
                  label="Message above the link"
                />
              </div>
            )}
          </fieldset>
          {touched && problem && <p className="text-destructive">{problem}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" onClick={save}>
              {initial ? "Save button" : "Add button"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** A phone with the keyboard where Telegram shows it: under the text box. */
function Preview({ footer }: { footer: Footer }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="border-b border-border px-3 py-2 font-medium">Preview</div>
      <div className="grid min-h-40 content-end gap-2 bg-[oklch(0.93_0.02_220)] p-3 dark:bg-[oklch(0.25_0.02_240)]">
        <div className="max-w-[85%] rounded-lg rounded-bl-sm bg-card px-3 py-2 shadow-sm">
          {filled(footer.intro) ? <TgHtml html={footer.intro} /> : DEFAULT_INTRO}
        </div>
      </div>
      <div className="border-t border-border bg-card px-3 py-2 text-muted-foreground">Message</div>
      {footer.rows.length > 0 && (
        <div className="grid gap-1 border-t border-border bg-muted/60 p-2">
          {footer.rows.map((row, r) => (
            <div key={r} className="flex gap-1">
              {row.map((b, i) => (
                <div
                  key={i}
                  className="min-w-0 flex-1 truncate rounded-md bg-card px-2 py-2 text-center shadow-sm"
                >
                  {b.text}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Icon({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-7 place-items-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-25 [&_svg]:size-3.5 [@media(pointer:coarse)]:size-9"
    >
      {children}
    </button>
  );
}

/** A short hint of the default text, for a translation's placeholder. */
function plainHint(html: string | undefined): string {
  const one = tgHtmlToPlain(html ?? "")
    .replace(/\s+/g, " ")
    .trim();
  return one.length > 90 ? `${one.slice(0, 90)}…` : one;
}

/** A bottom-menu button's words in one language. */
function TranslateDialog({
  button,
  words,
  onCancel,
  onSave,
}: {
  button: FooterButton;
  words: NonNullable<FooterTranslation["nodes"]>[string];
  onCancel: () => void;
  onSave: (words: NonNullable<FooterTranslation["nodes"]>[string]) => void;
}) {
  const [label, setLabel] = useState(words.label ?? "");
  const [message, setMessage] = useState(words.message ?? "");
  const [reply, setReply] = useState(words.reply ?? "");
  const a = button.action;
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/45" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed top-1/2 left-1/2 z-50 grid max-h-[90dvh] w-[min(440px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-lg border border-border bg-card p-4 shadow-xl"
        >
          <Dialog.Title className="font-semibold">Translate “{button.text}”</Dialog.Title>
          <Field label="Label">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={button.text}
              maxLength={64}
              autoFocus
            />
          </Field>
          {a.kind === "link" && (
            <Field group label="Message above the link">
              <RichText
                compact
                value={message}
                onChange={setMessage}
                placeholder={plainHint(a.message) || button.text}
                label="Message above the link"
              />
            </Field>
          )}
          {a.kind === "help" && (
            <Field group label="Reply to the player">
              <RichText
                compact
                value={reply}
                onChange={setReply}
                placeholder={plainHint(a.reply) || "Uses the default reply"}
                label="Reply to the player"
              />
            </Field>
          )}
          <p className="text-muted-foreground">
            Leave a field empty to use the default. Either label works when a player taps it.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() =>
                onSave({
                  label: label.trim() || undefined,
                  message: message || undefined,
                  reply: reply || undefined,
                })
              }
            >
              Save translation
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
